import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'

import { queryClient } from '~/app/providers/http'
import { TSessionDetails } from '~/features/auth/models'
import {
  queries,
  queryKeys,
  refreshAuthSessionQuery,
} from '~/features/auth/utils'
import HttpServiceBase, {
  getImpersonationHeaders,
  HttpQueryValue,
  HttpRequestBase,
  HttpServiceBaseResponse,
} from '~/services/http/models'
import { isAccessTokenExpiringSoon } from '~/utils/auth'

type TAuthRetryConfig = InternalAxiosRequestConfig & {
  authRetry?: boolean
}

enum EHttpMethod {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

function appendQueryParams(
  searchParams: URLSearchParams,
  query: Record<string, HttpQueryValue>,
): void {
  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v))
    } else {
      searchParams.append(key, value)
    }
  })
}

class AxiosHttpService implements HttpServiceBase {
  private axiosInstance: AxiosInstance

  constructor(name: string, baseURL: string) {
    if (!baseURL) {
      throw new Error(
        `Base URL for ${name} is missing. Did you forget to set the environment variable?`,
      )
    }

    this.axiosInstance = axios.create({
      baseURL,
      headers: {},
    })

    this.axiosInstance.interceptors.request.use(async (config) => {
      let session = queryClient.getQueryData<TSessionDetails>(
        queryKeys.authSession(),
      )

      if (!session) {
        session = await queryClient.ensureQueryData(queries.authSession())
      } else if (
        session.accessToken &&
        isAccessTokenExpiringSoon(session.accessTokenExpiresAt)
      ) {
        session = await refreshAuthSessionQuery()
      }

      const accessToken = session?.accessToken
      if (accessToken) {
        config.headers.set('Authorization', `Bearer ${accessToken}`)
      } else {
        config.headers.delete('Authorization')
      }

      const impersonationHeaders = getImpersonationHeaders()
      Object.entries(impersonationHeaders).forEach(([key, value]) => {
        if (!config.headers.has(key)) {
          config.headers.set(key, value)
        }
      })
      return config
    })

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config as TAuthRetryConfig | undefined

        if (error.response?.status !== 401 || !config || config.authRetry) {
          return Promise.reject(error)
        }

        config.authRetry = true
        await refreshAuthSessionQuery()

        return this.axiosInstance.request(config)
      },
    )
  }

  public getInterceptors() {
    return this.axiosInstance.interceptors
  }

  private buildUrl(
    path: string,
    query?: Record<string, HttpQueryValue>,
  ): string {
    const url = new URL(path, this.axiosInstance.defaults.baseURL)
    if (query) {
      appendQueryParams(url.searchParams, query)
    }
    return url.toString()
  }

  private async request<T>({
    headers,
    method,
    onUploadProgress,
    path,
    payload,
    query,
    signal,
  }: HttpRequestBase & { method: EHttpMethod }): Promise<
    HttpServiceBaseResponse<T>
  > {
    const url = this.buildUrl(path, query)

    const response: AxiosResponse<T> = await this.axiosInstance.request<T>({
      method,
      url,
      data: payload,
      headers,
      signal,
      ...(onUploadProgress
        ? {
            onUploadProgress: (e) =>
              onUploadProgress({ loaded: e.loaded, total: e.total }),
          }
        : {}),
    })

    return response.data as HttpServiceBaseResponse<T>
  }

  async get<T>(request: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>({ ...request, method: EHttpMethod.GET })
  }

  /** GET with `responseType: blob` (e.g. PDF exports). */
  async getBlob({ path, query, headers }: HttpRequestBase): Promise<Blob> {
    const url = this.buildUrl(path, query)
    try {
      const response = await this.axiosInstance.request<Blob>({
        method: EHttpMethod.GET,
        url,
        headers,
        responseType: 'blob',
      })
      return response.data
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
        const text = await err.response.data.text()
        let message = text.slice(0, 400)
        try {
          const parsed = JSON.parse(text) as { message?: unknown }
          if (typeof parsed.message === 'string') {
            message = parsed.message
          }
        } catch {
          /* keep text slice */
        }
        throw new Error(
          message ||
            err.message ||
            `Request failed (${String(err.response?.status ?? '?')})`,
        )
      }
      throw err
    }
  }

  async post<T>(request: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>({ ...request, method: EHttpMethod.POST })
  }

  async put<T>(request: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>({ ...request, method: EHttpMethod.PUT })
  }

  async patch<T>(
    request: HttpRequestBase,
  ): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>({ ...request, method: EHttpMethod.PATCH })
  }

  async delete<T>(
    request: HttpRequestBase,
  ): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>({ ...request, method: EHttpMethod.DELETE })
  }
}

export default AxiosHttpService
